import {
	doc,
	updateDoc,
	runTransaction,
	setDoc,
	collection,
	orderBy,
	limit,
	getDocs,
	query,
	deleteDoc,
	where,
	addDoc,
	writeBatch,
} from 'firebase/firestore';
import { useAuthStore } from '../../../../store/shared/auth-store';
import { db } from '@/firebase';
import i18n from '@/locales/index.js';
import { api } from '@/v2/plugins/axios';
import { callFirebaseFunction } from '@/v2/utils/firebase';

export default class Conversation {
	contact_display_name = undefined;
	profile_name = undefined;
	whatsapp_id = undefined;
	customer_id = undefined;
	timestamp = undefined;
	last_message = undefined;
	conversation_expiration_timestamp = undefined;
	conversation = undefined;
	channelType = undefined;
	labels = [];
	status = undefined;

	constructor(obj) {
		Object.keys(obj).forEach((key) => {
			this[key] = obj[key];
		});
	}

	get isWhatsapp() {
		return this.channelType === 'whatsapp';
	}

	get isInstagram() {
		return this.channelType === 'instagram';
	}

	get isWhatsappQR() {
		return this.channelType === 'whatsapp-qr';
	}

	get isMessenger() {
		return this.channelType === 'messenger';
	}

	get conversationId() {
		if (this.isWhatsapp || this.isWhatsappQR) return this.whatsapp_id;
		if (this.isInstagram || this.isMessenger) return this.customer_id;
		console.error('undefined conversation type', this);
		return null;
	}

	get expiration() {
		if (!this.conversation_expiration_timestamp) return { expiration: null };

		const expirationTimestamp = +this.conversation_expiration_timestamp;

		const expirationDateTime = new Date(expirationTimestamp * 1000);
		const now = new Date();
		const difference = expirationDateTime - now;
		const hoursRemaining = Math.floor(difference / 3600000);
		const minutesRemaining = Math.floor((difference % 3600000) / 60000);

		const hourSymbol = i18n.global.locale.value === 'ar' ? 'س' : 'h';
		const minuteSymbol = i18n.global.locale.value === 'ar' ? 'د' : 'm';

		let formattedTimeRemaining = '';
		let isExpired = false;

		if (hoursRemaining <= 0 && minutesRemaining >= 0) {
			formattedTimeRemaining = `${minutesRemaining} ${minuteSymbol}`;
		} else if (hoursRemaining > 0 && minutesRemaining < 0) {
			formattedTimeRemaining = `${hoursRemaining} ${hourSymbol}`;
		} else if (hoursRemaining > 0 && minutesRemaining >= 0) {
			formattedTimeRemaining = `${hoursRemaining} ${hourSymbol} ${minutesRemaining} ${minuteSymbol}`;
		} else {
			formattedTimeRemaining = i18n.global.t('conversation_session_expired');
			isExpired = true;
		}

		return {
			hoursRemaining,
			minutesRemaining,
			formattedTimeRemaining,
			isExpired,
		};
	}

	get conversationName() {
		return this.contact_display_name || this.profile_name;
	}

	get isLastMessageNew() {
		return this.last_message?.is_new;
	}

	get isLastFromMerchant() {
		return this.last_message?.sent_by_merchant;
	}

	get aiEnabled() {
		const conversationStatus = this.configs?.aiEnabled;
		if (typeof conversationStatus === 'boolean') {
			return conversationStatus;
		}
		const authStore = useAuthStore();
		return !!authStore.firebaseTenantConfig?.aiEnabled;
	}

	get isOpen() {
		return this.status === 'open' || this.status == null;
	}

	switchIsNewStatus() {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		const conversationRef = doc(
			db,
			`tenants/${tenantId}/channels/${this.channelType}/conversations`,
			this.conversationId,
		);
		updateDoc(conversationRef, {
			'last_message.is_new': !this.last_message?.is_new,
		});
	}

	setLabels(labels) {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		const conversationRef = doc(
			db,
			`tenants/${tenantId}/channels/${this.channelType}/conversations`,
			this.conversationId,
		);
		updateDoc(conversationRef, {
			labels,
		});
	}

	markLastMessageRead() {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		const conversationRef = doc(
			db,
			`tenants/${tenantId}/channels/${this.channelType}/conversations`,
			this.conversationId,
		);
		updateDoc(conversationRef, {
			'last_message.is_new': false,
		});
	}

	assignUserToConversation(userId, userName) {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		const conversationRef = doc(
			db,
			`tenants/${tenantId}/channels/${this.channelType}/conversations`,
			this.conversationId,
		);
		updateDoc(conversationRef, {
			assigned_to_user: {
				user_id: userId ?? 0,
				user_name: userName ?? '',
			},
		});
	}

	async updateConfigs(configs) {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;
		try {
			await runTransaction(db, async (transaction) => {
				const conversationRef = doc(
					db,
					`tenants/${tenantId}/channels/${this.channelType}/conversations`,
					this.conversationId,
				);
				const systemConversationRef = doc(
					db,
					`tenants/${tenantId}/system-messages`,
					this.conversationId,
				);
				await transaction.set(conversationRef, { configs }, { merge: true });
				await transaction.set(systemConversationRef, { configs }, { merge: true });
			});
		} catch (err) {
			console.error(err);
		}
	}

	async deleteConversation() {
		const authStore = useAuthStore();
		const tenantId = authStore.user.tenant.id;

		try {
			const messagesRef = collection(
				db,
				`tenants/${tenantId}/channels/${this.channelType}/conversations/${this.conversationId}/messages`,
			);
			const messagesSnapshot = await getDocs(query(messagesRef));

			const batchSize = 500;
			const messageDocs = messagesSnapshot.docs;

			for (let i = 0; i < messageDocs.length; i += batchSize) {
				const batch = writeBatch(db);
				const batchDocs = messageDocs.slice(i, i + batchSize);

				batchDocs.forEach((messageDoc) => {
					batch.delete(messageDoc.ref);
				});

				await batch.commit();
			}

			await runTransaction(db, async (transaction) => {
				const conversationRef = doc(
					db,
					`tenants/${tenantId}/channels/${this.channelType}/conversations`,
					this.conversationId,
				);

				const systemMessagesRef = doc(
					db,
					`tenants/${tenantId}/system-messages`,
					this.conversationId,
				);

				const aiSystemHistoryRef = doc(
					db,
					`tenants/${tenantId}/ai-system-history`,
					this.conversationId,
				);

				await transaction.delete(conversationRef);
				await transaction.delete(systemMessagesRef);
				await transaction.delete(aiSystemHistoryRef);
			});
		} catch (err) {
			console.error('Error deleting conversation:', err);
			throw err;
		}
	}

	async closeConversation() {
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId?.toString();
		const actorName = authStore.user?.full_name || authStore.user?.user_name || 'Unknown User';

		const payload = {
			tenantId,
			channelType: this.channelType,
			customerId: this.conversationId,
			actorName,
		};

		return await callFirebaseFunction('closeConversation', payload);
	}

	static sendAssignedNotification = async (data) => {
		return api.post('/v1/whatsapp/users/send-assigned-notification', { data });
	};

	async hasCustomerLastMessage() {
		const authStore = useAuthStore();
		const tenantId = `${authStore.user.tenant.id}`;
		const collectionRef = collection(
			db,
			`tenants/${tenantId}/system-messages/${this.conversationId}/messages`,
		);
		const q = query(
			collectionRef,
			where('sender', '==', 'user'),
			where('status', '==', 'pending'),
			orderBy('metadata.timestamp', 'desc'),
			limit(1),
		);
		const querySnapshot = await getDocs(q);
		return !querySnapshot.empty;
	}
   
	async rewriteLastSystemMessage() {
		const authStore = useAuthStore();
		const tenantId = `${authStore.user.tenant.id}`;
		const collectionRef = collection(
			db,
			`tenants/${tenantId}/system-messages/${this.conversationId}/messages`,
		);
		const q = query(
			collectionRef,
			where('sender', '==', 'user'),
			where('status', '==', 'pending'),
			orderBy('metadata.timestamp', 'desc'),
			limit(1),
		);
		const querySnapshot = await getDocs(q);
		if (querySnapshot.empty) return;
		const systemMessageDoc = querySnapshot.docs[0];
		const data = systemMessageDoc.data();
		deleteDoc(systemMessageDoc.ref);
		addDoc(collectionRef, {
			...data,
		});
	}
}
